import React, { Component } from "react";
import { AppBadge, LoadingDots } from "../../components/ui";
import { ClientTokenRequestData, MerchantClients } from "../../models/MerchantModel";
import { SelectItem } from "../../models/SelectItem";
import { toastService } from "../../services/toastService";
import { clientsApiService } from "../../services/api/clients/ClientsApiService";
import { Button } from "primereact/button";
import { utils } from "../../utils/utils";
import { isSuperAdminTeams } from "../../authGuard";
import { Copy, Download, Globe, KeyRound, Lock, Shield } from "lucide-react";

interface MerchantIntegrationProps {
    merchantId: number;
}

interface MerchantIntegrationState {
    items: MerchantClients[];
    isLoading: boolean;
    notificationUrl: string;
    notificationStatusUrl: string;
    notificationRefreshUrl: string;
    notification3DUrl: string;
}

export default class MerchantIntegration extends Component<MerchantIntegrationProps, MerchantIntegrationState> {
    contactTypes: SelectItem[] = [];

    constructor(props: MerchantIntegrationProps | Readonly<MerchantIntegrationProps>) {
        super(props);

        this.state = {
            items: [],
            isLoading: true,
            notification3DUrl: "",
            notificationRefreshUrl: "",
            notificationStatusUrl: "",
            notificationUrl: "",
        };
    }

    async componentDidMount() {
        const merchantClientsResult = await clientsApiService.getForMerchant(this.props.merchantId);

        if (!merchantClientsResult.success) {
            toastService.error(merchantClientsResult.message);
            this.setState({ isLoading: false });
            return;
        }

        this.setState({
            isLoading: false,
            items: merchantClientsResult.data,
        });
    }

    copyToClipboard = (val: string) => {
        navigator.clipboard.writeText(val);
        toastService.success("Text Copied.", "Action Completed");
    };

    downloadClientTokenFile = (rowData: MerchantClients) => {
        var data: ClientTokenRequestData = {
            client_id: rowData.clientId,
            certificate: rowData.secret,
            grant_type: "client_credentials",
            scope: "Tenn.CRM IdentityServerApi Tenn.RiskService Tenn.FileStorage",
        };
        utils.downloadMtlsTokenFile(data);
    };

    getAuthType = (client: MerchantClients) => {
        if (client.isMtlsClient) return { icon: Shield, label: "MTLS", description: "Mutual TLS Certificate-based", color: "text-primary-600 dark:text-primary-400", bg: "bg-primary-100 dark:bg-primary-900/30" };
        if (client.isOidcClient) return { icon: Lock, label: "OIDC", description: "OpenID Connect / OAuth 2.0 (PKCE)", color: "text-info-600 dark:text-info-400", bg: "bg-info-100 dark:bg-info-900/30" };
        return { icon: KeyRound, label: "Standard", description: "Standard API Client", color: "text-success-600 dark:text-success-400", bg: "bg-success-100 dark:bg-success-900/30" };
    };

    getSecretDisplay = (client: MerchantClients) => {
        if (client.isMtlsClient) return "Contact Admin for Certificate Access";
        if (client.isOidcClient) return "Contact Admin for Client Secret";
        return client.secret;
    };

    renderClientCard = (client: MerchantClients, index: number) => {
        const authType = this.getAuthType(client);
        const AuthIcon = authType.icon;
        const secretDisplay = this.getSecretDisplay(client);
        const canCopySecret = !client.isMtlsClient && !client.isOidcClient && client.secret;

        return (
            <div key={client.clientId || index} className={index > 0 ? "border-t border-neutral-200 dark:border-neutral-700" : ""}>
                {/* Client Header */}
                <div className="flex items-center justify-between px-4 py-4 bg-neutral-50/50 dark:bg-neutral-800/50 border-b border-neutral-100 dark:border-neutral-800">
                    <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${authType.bg} ${authType.color}`}>
                            <AuthIcon size={18} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 font-mono">{client.clientId}</span>
                                {client.isMtlsClient && <AppBadge variant="primary">MTLS</AppBadge>}
                                {client.isOidcClient && <AppBadge variant="info">OIDC</AppBadge>}
                            </div>
                            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                                {authType.description}
                            </div>
                        </div>
                    </div>
                    {client.isMtlsClient && isSuperAdminTeams() && (
                        <Button
                            label="Download Curl"
                            icon={<Download size={16} />}
                            size="small"
                            outlined
                            onClick={() => this.downloadClientTokenFile(client)}
                        />
                    )}
                </div>

                {/* Client Details */}
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                    {/* Client ID */}
                    <div className="flex items-center px-4 py-3">
                        <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 w-36 shrink-0">Client ID</span>
                        <span className="text-sm text-neutral-900 dark:text-neutral-100 font-mono">{client.clientId}</span>
                        <button
                            type="button"
                            className="ml-2 p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                            onClick={() => this.copyToClipboard(client.clientId)}
                            title="Copy Client ID"
                        >
                            <Copy size={14} className="text-neutral-400 hover:text-primary-500" />
                        </button>
                    </div>

                    {/* Secret / Certificate (hidden for OIDC — uses PKCE) */}
                    {!client.isOidcClient && (
                        <div className="flex items-center px-4 py-3">
                            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 w-36 shrink-0">
                                {client.isMtlsClient ? "Certificate" : "Client Secret"}
                            </span>
                            <span className={`text-sm ${canCopySecret ? "font-mono text-neutral-900 dark:text-neutral-100" : "text-neutral-500 dark:text-neutral-400 italic"}`}>
                                {secretDisplay}
                            </span>
                            {canCopySecret && (
                                <button
                                    type="button"
                                    className="ml-2 p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                                    onClick={() => this.copyToClipboard(client.secret ?? "")}
                                    title="Copy Secret"
                                >
                                    <Copy size={14} className="text-neutral-400 hover:text-primary-500" />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Allowed Origins */}
                    <div className="flex items-start px-4 py-3">
                        <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 w-36 shrink-0 pt-0.5">
                            {client.isOidcClient ? "CORS Origins" : "Allowed Origins"}
                        </span>
                        <div className="flex-1">
                            {client.allowedCorsOrigins.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {client.allowedCorsOrigins.map((origin, idx) => (
                                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-400 border border-success-200 dark:border-success-800">
                                            <Globe size={11} />
                                            {origin}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-sm text-neutral-500 dark:text-neutral-400 italic">
                                    {client.isOidcClient ? "Configured via OIDC settings" : "All hostnames are allowed"}
                                </span>
                            )}
                        </div>
                    </div>
                    {/* Redirect URIs */}
                    {client.redirectUris?.length > 0 && (
                        <div className="flex items-start px-4 py-3">
                            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 w-36 shrink-0 pt-0.5">
                                Redirect URIs
                            </span>
                            <div className="flex-1 flex flex-wrap gap-1.5">
                                {client.redirectUris.map((uri, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 border border-primary-200 dark:border-primary-800">
                                        <Globe size={11} />
                                        {uri}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Post Logout URIs */}
                    {client.postLogoutRedirectUris?.length > 0 && (
                        <div className="flex items-start px-4 py-3">
                            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 w-36 shrink-0 pt-0.5">
                                Post Logout URIs
                            </span>
                            <div className="flex-1 flex flex-wrap gap-1.5">
                                {client.postLogoutRedirectUris.map((uri, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                                        <Globe size={11} />
                                        {uri}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    render() {
        if (this.state.isLoading) {
            return (
                <div className="min-h-[300px] flex items-center justify-center">
                    <LoadingDots />
                </div>
            );
        }

        if (this.state.items.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-neutral-400 dark:text-neutral-500">
                    <KeyRound size={40} strokeWidth={1.5} />
                    <p className="mt-3 text-sm">No API clients configured for this merchant.</p>
                </div>
            );
        }

        return (
            <div>
                {this.state.items.map((client, idx) => this.renderClientCard(client, idx))}
            </div>
        );
    }
}
